import { writeBatch, doc, collection, serverTimestamp, updateDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { createLogger } from './logger';

const logger = createLogger('firestoreBatch');

const FIRESTORE_BATCH_LIMIT = 500;

/**
 * Executes Firestore operations in batches to avoid rate limiting and improve performance.
 * 
 * @param {Array} operations - Array of operation objects
 * @param {Object} db - Firestore database instance
 * @returns {Promise<Object>} Results object with success and failed operations
 * 
 * Operation format:
 * {
 *   type: 'update' | 'set' | 'delete',
 *   collection: string,
 *   id: string,
 *   data: object (for update/set operations),
 *   metadata: object (optional, passed through to results for tracking)
 * }
 */
export async function executeBatchOperations(operations, db) {
  if (!operations || !Array.isArray(operations)) {
    throw new Error('Operations must be an array');
  }

  if (!db) {
    throw new Error('Firestore database instance is required');
  }

  if (operations.length === 0) {
    return { success: [], failed: [] };
  }

  const results = {
    success: [],
    failed: [],
  };

  const totalBatches = Math.ceil(operations.length / FIRESTORE_BATCH_LIMIT);
  logger.info(`[executeBatchOperations] Processing ${operations.length} operations in ${totalBatches} batch${totalBatches !== 1 ? 'es' : ''}`);

  for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
    const startIndex = batchIndex * FIRESTORE_BATCH_LIMIT;
    const endIndex = Math.min(startIndex + FIRESTORE_BATCH_LIMIT, operations.length);
    const batchOps = operations.slice(startIndex, endIndex);

    logger.info(`[executeBatchOperations] Processing batch ${batchIndex + 1}/${totalBatches} (${batchOps.length} operations)`);

    const batch = writeBatch(db);
    const batchOperationTrackers = [];

    for (const operation of batchOps) {
      try {
        validateOperation(operation);

        const docRef = doc(db, operation.collection, operation.id);
        
        switch (operation.type) {
          case 'update':
            batch.update(docRef, operation.data);
            break;
          case 'set':
            batch.set(docRef, operation.data);
            break;
          case 'delete':
            batch.delete(docRef);
            break;
          default:
            throw new Error(`Unknown operation type: ${operation.type}`);
        }

        batchOperationTrackers.push({
          operation,
          status: 'pending',
        });
      } catch (validationError) {
        logger.error(`[executeBatchOperations] Invalid operation:`, operation, validationError);
        results.failed.push({
          operation,
          error: validationError.message,
          metadata: operation.metadata,
        });
      }
    }

    try {
      await batch.commit();
      
      logger.info(`[executeBatchOperations] Batch ${batchIndex + 1}/${totalBatches} committed successfully`);

      for (const tracker of batchOperationTrackers) {
        results.success.push({
          operation: tracker.operation,
          metadata: tracker.operation.metadata,
        });
      }
    } catch (batchError) {
      // The batch is atomic: a single bad operation (e.g. updating a document
      // that no longer exists) rolls back the entire batch and would silently
      // drop up to FIRESTORE_BATCH_LIMIT-1 otherwise-valid writes.  Fall back
      // to individual writes so each operation is isolated.
      logger.warn(
        `[executeBatchOperations] Batch ${batchIndex + 1}/${totalBatches} failed (${batchError.message}), falling back to individual operations`,
      );

      for (const tracker of batchOperationTrackers) {
        try {
          const { operation } = tracker;
          const fallbackRef = doc(db, operation.collection, operation.id);

          switch (operation.type) {
            case 'update':
              await updateDoc(fallbackRef, operation.data);
              break;
            case 'set':
              await setDoc(fallbackRef, operation.data);
              break;
            case 'delete':
              await deleteDoc(fallbackRef);
              break;
            default:
              throw new Error(`Unknown operation type: ${operation.type}`);
          }

          results.success.push({
            operation: tracker.operation,
            metadata: tracker.operation.metadata,
          });
        } catch (individualError) {
          logger.error(
            `[executeBatchOperations] Individual fallback failed for ${tracker.operation.id}:`,
            individualError.message,
          );
          results.failed.push({
            operation: tracker.operation,
            error: individualError.message,
            metadata: tracker.operation.metadata,
          });
        }
      }
    }
  }

  logger.info(`[executeBatchOperations] Completed: ${results.success.length} successful, ${results.failed.length} failed`);

  return results;
}

/**
 * Validates an operation object
 * @param {Object} operation - Operation to validate
 * @throws {Error} If operation is invalid
 */
function validateOperation(operation) {
  if (!operation || typeof operation !== 'object') {
    throw new Error('Operation must be an object');
  }

  if (!operation.type || !['update', 'set', 'delete'].includes(operation.type)) {
    throw new Error(`Invalid operation type: ${operation.type}. Must be 'update', 'set', or 'delete'`);
  }

  if (!operation.collection || typeof operation.collection !== 'string') {
    throw new Error('Operation must have a valid collection name');
  }

  if (!operation.id || typeof operation.id !== 'string') {
    throw new Error('Operation must have a valid document ID');
  }

  if ((operation.type === 'update' || operation.type === 'set') && !operation.data) {
    throw new Error(`Operation type '${operation.type}' requires data field`);
  }

  if (operation.data && typeof operation.data !== 'object') {
    throw new Error('Operation data must be an object');
  }
}

/**
 * Helper function to create update operations for inventory items
 * @param {Array} items - Array of inventory update items
 * @param {Object} userInfo - Current user information
 * @returns {Array} Array of operation objects
 */
export function createInventoryUpdateOperations(items, userInfo) {
  return items.map((item) => ({
    type: 'update',
    collection: 'Inventory',
    id: item.id,
    data: {
      quantity: item.quantity,
      sale_price: item.sale_price,
      product_upc: item.upc,
      updated_by_uid: userInfo.uid,
      updated_by_email: userInfo.email || 'Unknown User',
      updated_at: new Date(),
    },
    metadata: {
      upc: item.upc,
      productName: item.productName,
      action: 'update',
    },
  }));
}

/**
 * Helper function to create set operations for new inventory items
 * @param {Array} items - Array of inventory addition items
 * @param {Object} db - Firestore database instance
 * @param {Object} userInfo - Current user information
 * @returns {Array} Array of operation objects
 */
export function createInventoryAdditionOperations(items, db, userInfo) {
  return items.map((item) => {
    const inventoryCollection = collection(db, 'Inventory');
    const newDocRef = doc(inventoryCollection);
    
    return {
      type: 'set',
      collection: 'Inventory',
      id: newDocRef.id,
      data: {
        supermarket_id: item.supermarketId,
        supermarket_name: item.supermarketName,
        product_id: item.productId,
        product_name: item.productName,
        product_upc: item.productUpc,
        sale_price: item.sale_price,
        quantity: item.quantity,
        discount_deals: [],
        created_by_uid: userInfo.uid,
        created_by_email: userInfo.email || 'Unknown User',
        created_at: serverTimestamp(),
      },
      metadata: {
        upc: item.productUpc,
        productName: item.productName,
        action: 'add',
        newDocId: newDocRef.id,
      },
    };
  });
}
